/**
 * 境外服務收費計算機
 *
 * 稅金僅對貨款（台幣換算）課徵；手續費／匯費不課稅。
 * 費率常數為單一來源，畫面與計算共用。
 */

export const OVERSEAS_RATES = {
  cardFee: 0.03,
  nrTax: 0.2,
  service: [0.1, 0.05] as const,
};

export type FeeMode = 'none' | 'card' | 'wire';

export interface OverseasCurrency {
  code: string;
  label: string;
  symbol: string;
  rate: number | null;
}

export const OVERSEAS_CURRENCIES: OverseasCurrency[] = [
  { code: 'USD', label: 'USD 美元', symbol: '$', rate: 31.5 },
  { code: 'EUR', label: 'EUR 歐元', symbol: '€', rate: 34.8 },
  { code: 'GBP', label: 'GBP 英鎊', symbol: '£', rate: 40.2 },
  { code: 'JPY', label: 'JPY 日圓', symbol: '¥', rate: 0.21 },
  { code: 'CNY', label: 'CNY 人民幣', symbol: '¥', rate: 4.35 },
  { code: 'CUSTOM', label: '自訂', symbol: '', rate: null },
];

export interface OverseasCalculatorInput {
  amount: number | string;
  rate: number | string;
  feeMode: FeeMode;
  wireFee: number | string;
  taxOn: boolean;
  serviceRate: number;
}

export interface OverseasCalculatorResult {
  base: number;
  tax: number;
  cardFee: number;
  wire: number;
  subtotal: number;
  service: number;
  total: number;
}

export interface OverseasBreakdownRow {
  label: string;
  note?: string;
  val: number;
  add?: boolean;
  isSub?: boolean;
}

export interface OverseasCopyContext {
  code: string;
  amt: number;
  r: number;
  total: number;
  markup: number;
}

/** 解析為正數；非有限或 ≤ 0 視為 0 */
export function toPositiveNumber(value: number | string): number {
  const n = typeof value === 'number' ? value : parseFloat(value);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

export function formatTwdNumber(n: number): string {
  return (Number.isFinite(n) ? n : 0).toLocaleString('zh-TW', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

export function formatTwd(n: number): string {
  return `TWD ${formatTwdNumber(n)}`;
}

export function formatPercent(rate: number): string {
  return `${+(rate * 100).toFixed(4)}%`;
}

export function nrTaxDivisorLabel(): string {
  return (1 - OVERSEAS_RATES.nrTax).toFixed(2);
}

/**
 * base    = 金額 × 匯率
 * tax     = base ÷ 0.8 − base（只對 base 課，可關閉）
 * fee     = 卡費(base×3%) 或 匯費(固定 TWD)，二擇一，不課稅
 * subtotal= base + tax + fee
 * service = subtotal × (10% 或 5%)
 * total   = subtotal + service
 */
export function calculateOverseasServiceFee(
  input: OverseasCalculatorInput
): OverseasCalculatorResult {
  const base = toPositiveNumber(input.amount) * toPositiveNumber(input.rate);
  const tax = input.taxOn
    ? base * (OVERSEAS_RATES.nrTax / (1 - OVERSEAS_RATES.nrTax))
    : 0;
  const cardFee = input.feeMode === 'card' ? base * OVERSEAS_RATES.cardFee : 0;
  const wire = input.feeMode === 'wire' ? toPositiveNumber(input.wireFee) : 0;
  const subtotal = base + tax + cardFee + wire;
  const service = subtotal * input.serviceRate;
  return { base, tax, cardFee, wire, subtotal, service, total: subtotal + service };
}

export function markupPercent(total: number, base: number): number {
  return base > 0 ? ((total - base) / base) * 100 : 0;
}

export function buildOverseasBreakdownRows(
  result: OverseasCalculatorResult,
  options: {
    code: string;
    amount: number;
    rate: number;
    feeMode: FeeMode;
    taxOn: boolean;
    serviceRate: number;
  }
): OverseasBreakdownRow[] {
  const rows: OverseasBreakdownRow[] = [
    {
      label: '台幣換算',
      note: `${options.code} ${formatTwdNumber(options.amount)} × ${options.rate}`,
      val: result.base,
    },
  ];
  if (options.taxOn) {
    rows.push({
      label: `非居住者稅還原 ÷ ${nrTaxDivisorLabel()}`,
      note: '僅對台幣換算金額課徵',
      val: result.tax,
      add: true,
    });
  }
  if (options.feeMode === 'card') {
    rows.push({
      label: `信用卡手續費 ${formatPercent(OVERSEAS_RATES.cardFee)}`,
      note: '以台幣換算金額計，不課稅',
      val: result.cardFee,
      add: true,
    });
  }
  if (options.feeMode === 'wire') {
    rows.push({
      label: '匯費（固定）',
      note: '不課稅',
      val: result.wire,
      add: true,
    });
  }
  rows.push({ label: '小計', val: result.subtotal, isSub: true });
  rows.push({
    label: `服務費 ${formatPercent(options.serviceRate)}`,
    note: '以小計為基礎',
    val: result.service,
    add: true,
  });
  return rows;
}

export function buildOverseasCopyText(
  ctx: OverseasCopyContext,
  rows: OverseasBreakdownRow[]
): string {
  return [
    '【境外服務收費計算】',
    `幣別：${ctx.code}`,
    `購買金額：${formatTwdNumber(ctx.amt)} ${ctx.code}`,
    `匯率：${ctx.r}`,
    '──────────────',
    ...rows.map(
      (rw) =>
        `${rw.add ? '＋' : rw.isSub ? '＝' : '　'} ${rw.label}：TWD ${formatTwdNumber(rw.val)}`
    ),
    '──────────────',
    `應收金額：TWD ${formatTwdNumber(ctx.total)}`,
    `有效加成率：+${ctx.markup.toFixed(1)}%`,
  ].join('\n');
}
